import Microgame from '../engine/Microgame.js';

export default class UnrollGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Déroule !`, en: `Unroll!`, es: `¡Desenrolla!` },
            controls: { fr: `Glisse ton doigt plein de fois vers le bas`, en: `Swipe down repeatedly`, es: `Desliza hacia abajo repetidamente` },
            objective: { fr: `Vide complètement le rouleau de PQ`, en: `Empty the toilet paper roll completely`, es: `Vacía completamente el rollo de papel` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><path d="M4 14V6c0-1.1.9-2 2-2h12c1.1 0 2 .9 2 2v8"/><path d="M4 14h16v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-6Z"/><path d="M12 4v20"/></svg>`,
            actions: [
                { type: 'wait', duration: 260 },
                { type: 'swipe', fromX: 22, fromY: 76, toX: 30, toY: 18, duration: 620 },
                { type: 'swipe', fromX: 32, fromY: 28, toX: 56, toY: 32, duration: 620 },
                { type: 'swipe', fromX: 58, fromY: 36, toX: 72, toY: 20, duration: 620 },
                { type: 'wait', duration: 520 },
            ],
            loopDuration: 3600
        };
    }
    constructor(engine) {
        super(engine);

        this.isDragging = false;
        this.canvas = null;
        this.ctx = null;

        this.endX = 0;
        this.endY = 0;
        this.startDragX = 0;
        this.startDragY = 0;
        this.startEndX = 0;
        this.startEndY = 0;

        this.hooks = [];
        this.hookedOrder = []; // Points where rope was hooked, in player's chosen order
        this.pileX = 0;
        this.pileY = 0;
        this.coils = [];

        this.isComplete = false;
        this.unrollProgress = 0;

        this._isAnimating = false;
        this._frozenTimerValue = 0;

        const speedMult = engine.speedMultiplier || 1.0;
        const adjustedSpeedScale = 1 + (speedMult - 1) * 0.5;
        this.duration = 5000 * (speedMult / adjustedSpeedScale);
        this.hookSnapRadius = 30;
    }

    setup() {
        this.engine.ui.instruction.textContent = "HOOK IT!";

        this.canvas = document.createElement('canvas');
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.width = '100%';
        this.canvas.style.height = '100%';
        this.canvas.style.cursor = 'grab';

        const rect = this.container.getBoundingClientRect();
        this.W = rect.width || 400;
        this.H = rect.height || 700;
        this.canvas.width = this.W;
        this.canvas.height = this.H;
        this.container.appendChild(this.canvas);
        this.ctx = this.canvas.getContext('2d');

        // Pile (bottom-left)
        this.pileX = this.isPlayback ? this.W * 0.22 : (this.W * 0.13 + Math.random() * this.W * 0.06);
        this.pileY = this.isPlayback ? this.H * 0.82 : (this.H * 0.78 + Math.random() * this.H * 0.05);
        this.endX = this.pileX + 35;
        this.endY = this.pileY - 25;

        // Generate 3 hooks at TRULY random positions with spacing
        this.hooks = [];
        const margin = 60;
        const minDist = 90;
        if (this.isPlayback) {
            this.hooks = [
                { x: this.W * 0.3, y: this.H * 0.16, reached: false },
                { x: this.W * 0.56, y: this.H * 0.3, reached: false },
                { x: this.W * 0.72, y: this.H * 0.18, reached: false }
            ];
        } else {
        for (let i = 0; i < 3; i++) {
            let attempts = 0;
            let hx, hy;
            do {
                hx = margin + Math.random() * (this.W - margin * 2);
                hy = this.H * 0.12 + Math.random() * (this.H * 0.55);
                attempts++;
            } while (attempts < 50 && (
                this.hooks.some(h => Math.sqrt((h.x - hx) ** 2 + (h.y - hy) ** 2) < minDist) ||
                Math.sqrt((this.pileX - hx) ** 2 + (this.pileY - hy) ** 2) < minDist
            ));
            this.hooks.push({ x: hx, y: hy, reached: false });
        }}

        // Pile coils
        this.coils = [];
        const coilCount = 4 + Math.floor(Math.random() * 3);
        for (let i = 0; i < coilCount; i++) {
            this.coils.push({
                ox: (Math.random() - 0.5) * 45,
                oy: (Math.random() - 0.5) * 35 - i * 4,
                rx: 15 + Math.random() * 18,
                ry: 8 + Math.random() * 10,
                rot: Math.random() * Math.PI
            });
        }

        this.hookedOrder = [];

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);

        this.draw();
    }

    // Check if two line segments (p1-p2) and (p3-p4) intersect
    segmentsIntersect(p1, p2, p3, p4) {
        const d1 = this.cross(p3, p4, p1);
        const d2 = this.cross(p3, p4, p2);
        const d3 = this.cross(p1, p2, p3);
        const d4 = this.cross(p1, p2, p4);
        if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
            ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
            return true;
        }
        return false;
    }

    cross(a, b, c) {
        return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
    }

    checkRopeCrossing() {
        // Build full path: pile -> hooked points in order
        const pilePoint = { x: this.pileX, y: this.pileY - 25 };
        const points = [pilePoint, ...this.hookedOrder];

        // Build segments
        const segments = [];
        for (let i = 0; i < points.length - 1; i++) {
            segments.push([points[i], points[i + 1]]);
        }

        // Check all non-adjacent segment pairs
        for (let i = 0; i < segments.length; i++) {
            for (let j = i + 2; j < segments.length; j++) {
                if (this.segmentsIntersect(segments[i][0], segments[i][1], segments[j][0], segments[j][1])) {
                    return true; // Crossed!
                }
            }
        }
        return false;
    }

    draw() {
        const ctx = this.ctx;
        ctx.clearRect(0, 0, this.W, this.H);
        const t = 9;

        // Draw hooks
        for (let i = 0; i < this.hooks.length; i++) {
            this.drawHook(ctx, this.hooks[i]);
        }

        if (this.isComplete) {
            this.drawCompletedRope(ctx, t);
            return;
        }

        this.drawPile(ctx, t);
        this.drawRopeSegments(ctx, t);
        this.drawRopeEnd(ctx, t);

        // Grab hint
        if (!this.isDragging && !this.isComplete) {
            const pulse = 0.3 + Math.sin(Date.now() / 250) * 0.2;
            ctx.save();
            ctx.globalAlpha = pulse;
            ctx.strokeStyle = this.secondaryColor;
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(this.endX, this.endY, 22, 0, Math.PI * 2);
            ctx.stroke();
            ctx.restore();
        }

        // Snap zone hints on unreached hooks
        if (this.isDragging) {
            for (const hook of this.hooks) {
                if (hook.reached) continue;
                const snapY = hook.y + 40;
                const dist = Math.sqrt((this.endX - hook.x) ** 2 + (this.endY - snapY) ** 2);
                if (dist < this.hookSnapRadius * 3) {
                    const alpha = Math.max(0, 0.45 - (dist / (this.hookSnapRadius * 3)) * 0.45);
                    ctx.save();
                    ctx.globalAlpha = alpha;
                    ctx.strokeStyle = this.secondaryColor;
                    ctx.lineWidth = 3;
                    ctx.setLineDash([4, 4]);
                    ctx.beginPath();
                    ctx.arc(hook.x, snapY, this.hookSnapRadius, 0, Math.PI * 2);
                    ctx.stroke();
                    ctx.restore();
                }
            }
        }
    }

    drawHook(ctx, hook) {
        const hx = hook.x;
        const hy = hook.y;
        const isReached = hook.reached;

        ctx.save();

        // Wall mount
        ctx.fillStyle = isReached ? this.primaryColor : this.secondaryColor;
        ctx.fillRect(hx - 4, hy - 12, 8, 16);

        // Hook (J shape)
        ctx.strokeStyle = isReached ? this.primaryColor : this.secondaryColor;
        ctx.lineWidth = 6;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(hx, hy);
        ctx.lineTo(hx, hy + 25);
        ctx.arc(hx - 10, hy + 25, 10, 0, Math.PI, false);
        ctx.stroke();

        // Hook tip
        ctx.fillStyle = ctx.strokeStyle;
        ctx.beginPath();
        ctx.arc(hx - 20, hy + 25, 4, 0, Math.PI * 2);
        ctx.fill();

        // Glow on unreached hooks
        if (!isReached) {
            const glow = 0.1 + Math.sin(Date.now() / 350 + hx) * 0.08;
            ctx.globalAlpha = glow;
            ctx.fillStyle = this.secondaryColor;
            ctx.beginPath();
            ctx.arc(hx, hy + 20, 22, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.restore();
    }

    drawPile(ctx, t) {
        ctx.save();
        ctx.lineCap = 'round';
        for (let i = 0; i < this.coils.length; i++) {
            const c = this.coils[i];
            const cx = this.pileX + c.ox;
            const cy = this.pileY + c.oy;

            ctx.strokeStyle = 'rgba(0,0,0,0.1)';
            ctx.lineWidth = t + 3;
            ctx.beginPath();
            ctx.ellipse(cx + 2, cy + 2, c.rx, c.ry, c.rot, 0, Math.PI * 2);
            ctx.stroke();

            ctx.strokeStyle = this.primaryColor;
            ctx.lineWidth = t;
            ctx.beginPath();
            ctx.ellipse(cx, cy, c.rx, c.ry, c.rot, 0, Math.PI * 2);
            ctx.stroke();
        }
        ctx.restore();
    }

    drawRopeSegments(ctx, t) {
        ctx.save();
        ctx.lineCap = 'round';

        const points = [{ x: this.pileX, y: this.pileY - 25 }];
        for (const hp of this.hookedOrder) points.push(hp);
        points.push({ x: this.endX, y: this.endY });

        for (let i = 0; i < points.length - 1; i++) {
            const from = points[i];
            const to = points[i + 1];
            const dist = Math.sqrt((to.x - from.x) ** 2 + (to.y - from.y) ** 2);
            const sag = Math.min(dist * 0.22, 45);
            const midX = (from.x + to.x) / 2;
            const midY = Math.max(from.y, to.y) + sag;

            ctx.strokeStyle = 'rgba(0,0,0,0.1)';
            ctx.lineWidth = t + 3;
            ctx.beginPath();
            ctx.moveTo(from.x + 1, from.y + 2);
            ctx.quadraticCurveTo(midX + 1, midY + 2, to.x + 1, to.y + 2);
            ctx.stroke();

            ctx.strokeStyle = this.primaryColor;
            ctx.lineWidth = t;
            ctx.beginPath();
            ctx.moveTo(from.x, from.y);
            ctx.quadraticCurveTo(midX, midY, to.x, to.y);
            ctx.stroke();
        }
        ctx.restore();
    }

    drawRopeEnd(ctx, t) {
        ctx.save();
        ctx.fillStyle = 'rgba(0,0,0,0.15)';
        ctx.beginPath();
        ctx.arc(this.endX + 1, this.endY + 2, t, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = this.primaryColor;
        ctx.beginPath();
        ctx.arc(this.endX, this.endY, t, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.25)';
        ctx.beginPath();
        ctx.arc(this.endX - 2, this.endY - 2, t * 0.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }

    drawCompletedRope(ctx, t) {
        const progress = Math.min(this.unrollProgress, 1);
        ctx.save();
        ctx.lineCap = 'round';

        // Fading pile
        const coilsLeft = Math.floor(this.coils.length * (1 - progress));
        for (let i = 0; i < coilsLeft; i++) {
            const c = this.coils[i];
            ctx.globalAlpha = 0.5 * (1 - progress);
            ctx.strokeStyle = this.primaryColor;
            ctx.lineWidth = t;
            ctx.beginPath();
            ctx.ellipse(this.pileX + c.ox, this.pileY + c.oy, c.rx * (1 - progress * 0.5), c.ry * (1 - progress * 0.5), c.rot, 0, Math.PI * 2);
            ctx.stroke();
        }
        ctx.globalAlpha = 1;

        // Full path
        const allPoints = [{ x: this.pileX, y: this.pileY - 25 }, ...this.hookedOrder];
        const totalSeg = allPoints.length;
        const drawn = Math.min(totalSeg, Math.floor(progress * (totalSeg + 1)));

        for (let i = 0; i < drawn && i < allPoints.length - 1; i++) {
            const from = allPoints[i];
            const to = allPoints[i + 1];
            const dist = Math.sqrt((to.x - from.x) ** 2 + (to.y - from.y) ** 2);
            const sag = Math.min(dist * 0.18, 35);
            const midX = (from.x + to.x) / 2;
            const midY = Math.max(from.y, to.y) + sag;

            ctx.strokeStyle = 'rgba(0,0,0,0.1)';
            ctx.lineWidth = t + 3;
            ctx.beginPath();
            ctx.moveTo(from.x + 1, from.y + 2);
            ctx.quadraticCurveTo(midX + 1, midY + 2, to.x + 1, to.y + 2);
            ctx.stroke();

            ctx.strokeStyle = this.primaryColor;
            ctx.lineWidth = t;
            ctx.beginPath();
            ctx.moveTo(from.x, from.y);
            ctx.quadraticCurveTo(midX, midY, to.x, to.y);
            ctx.stroke();
        }

        // Hanging tail
        if (progress > 0.5) {
            const last = allPoints[allPoints.length - 1];
            const tp = (progress - 0.5) * 2;
            const tLen = 100 * tp;
            const sw = Math.sin(Date.now() / 200) * 10 * Math.max(0, 1 - tp * 0.7);

            ctx.strokeStyle = 'rgba(0,0,0,0.1)';
            ctx.lineWidth = t + 3;
            ctx.beginPath();
            ctx.moveTo(last.x + 1, last.y + 2);
            ctx.quadraticCurveTo(last.x + sw + 1, last.y + tLen * 0.6 + 2, last.x + sw * 0.5 + 1, last.y + tLen + 2);
            ctx.stroke();

            ctx.strokeStyle = this.primaryColor;
            ctx.lineWidth = t;
            ctx.beginPath();
            ctx.moveTo(last.x, last.y);
            ctx.quadraticCurveTo(last.x + sw, last.y + tLen * 0.6, last.x + sw * 0.5, last.y + tLen);
            ctx.stroke();

            ctx.fillStyle = this.primaryColor;
            ctx.beginPath();
            ctx.arc(last.x + sw * 0.5, last.y + tLen, t / 2, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.restore();
    }

    update(dt) {
        if (this._isAnimating) {
            this.engine.timer = Math.max(0, this._frozenTimerValue);
        }
        if (this.isComplete) {
            this.unrollProgress += (dt / 1000) * 2.5;
        }
        this.draw();
    }

    onDown(e) {
        if (this.won || this.lost || this.isComplete) return;
        this.isDragging = true;
        this.startDragX = e.clientX;
        this.startDragY = e.clientY;
        this.startEndX = this.endX;
        this.startEndY = this.endY;
        this.canvas.style.cursor = 'grabbing';
    }

    onMove(e) {
        if (!this.isDragging || this.won || this.lost || this.isComplete) return;

        this.endX = this.startEndX + (e.clientX - this.startDragX);
        this.endY = this.startEndY + (e.clientY - this.startDragY);
        this.endX = Math.max(10, Math.min(this.W - 10, this.endX));
        this.endY = Math.max(10, Math.min(this.H - 10, this.endY));

        // Check proximity to ANY unreached hook
        for (const hook of this.hooks) {
            if (hook.reached) continue;
            const snapX = hook.x;
            const snapY = hook.y + 40;
            const dist = Math.sqrt((this.endX - snapX) ** 2 + (this.endY - snapY) ** 2);

            if (dist < this.hookSnapRadius) {
                hook.reached = true;
                this.hookedOrder.push({ x: snapX, y: snapY });
                this.endX = snapX + 15;
                this.endY = snapY + 10;
                this.startDragX = e.clientX;
                this.startDragY = e.clientY;
                this.startEndX = this.endX;
                this.startEndY = this.endY;

                // All 3 hooked?
                if (this.hookedOrder.length >= 3) {
                    this._isAnimating = true;
                    this._frozenTimerValue = this.engine.timer;

                    if (this.checkRopeCrossing()) {
                        // Rope is tangled! Lose
                        setTimeout(() => this.lose(), 400);
                    } else {
                        // Clean path! Win
                        this.isComplete = true;
                        this.unrollProgress = 0;
                        setTimeout(() => this.win(), 800);
                    }
                }
                break; // Only one hook per move
            }
        }
    }

    onUp() {
        this.isDragging = false;
        if (this.canvas) this.canvas.style.cursor = 'grab';
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
